import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { RVP_CONTENT, RVP_LIST } from '../data/rendezvousPedagogiques';
import { SLIDE_REGISTRY } from '../components/RvpSlides';
import RvpTestPattern from '../components/RvpTestPattern';
import SunModeToggle from '../components/SunModeToggle';
import { ShieldCheck, Maximize2, Projector, MoveRight } from 'lucide-react';

/**
 * Vue "vitrine" pour le vidéoprojecteur (2e écran).
 * Reçoit la position + participants + commande fullscreen via BroadcastChannel.
 */
export default function RvpPublicView() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const rvp = useMemo(() => RVP_LIST.find((r) => r.slug === slug), [slug]);
  const rawSlides = useMemo(() => RVP_CONTENT[slug] || [], [slug]);
  const [current, setCurrent] = useState(0);
  const [revealMap, setRevealMap] = useState({});
  const [participants, setParticipants] = useState({ eleves: [] });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasReceivedSync, setHasReceivedSync] = useState(false);
  const [showHelper, setShowHelper] = useState(true);
  const [showTestPattern, setShowTestPattern] = useState(searchParams.get('debug') === 'mire');
  const [textZoom, setTextZoom] = useState(1);
  const [joinCode, setJoinCode] = useState(null);
  const bcRef = useRef(null);

  const formationType = slug === 'am-synthese' ? 'AM' : (slug?.startsWith('aac-') ? 'AAC' : null);

  const slides = useMemo(() => {
    if (!participants.eleves || participants.eleves.length === 0) return rawSlides;
    const bienvenue = {
      type: 'bienvenue-perso',
      title: rvp?.title ? `Bienvenue · ${rvp.title}` : 'Bienvenue',
      subtitle: 'Prêts pour un moment d\'échange et de partage ensemble ?',
      eleves: participants.eleves,
    };
    const merci = {
      type: 'merci-perso',
      subtitle: 'À très vite pour la suite de la formation. Roulez prudemment !',
      next_action: 'À la prochaine séance',
      eleves: participants.eleves,
    };
    return [bienvenue, ...rawSlides, merci];
  }, [participants.eleves, rawSlides, rvp]);

  const goFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      }
      setShowHelper(false);
    } catch {
      // Si l'appel programmatique échoue (nécessite un geste utilisateur),
      // le gros bouton "Passer en plein écran" reste visible.
    }
  }, []);

  useEffect(() => {
    const bc = new BroadcastChannel(`rvp-sync-${slug}`);
    bcRef.current = bc;
    bc.onmessage = (event) => {
      const data = event.data || {};
      if (typeof data.slide === 'number') {
        setCurrent(data.slide);
        setHasReceivedSync(true);
      }
      if (data.revealMap && typeof data.revealMap === 'object') {
        setRevealMap(data.revealMap);
      }
      if (data.participants && Array.isArray(data.participants.eleves)) {
        setParticipants(data.participants);
      }
      if (typeof data.textZoom === 'number') {
        setTextZoom(Math.min(2, Math.max(1, data.textZoom)));
      }
      if ('spectateurCode' in data) {
        setJoinCode(data.spectateurCode || null);
      }
      if (data.cmd === 'goFullscreen') {
        goFullscreen();
      }
      if (data.cmd === 'exitFullscreen') {
        try { if (document.fullscreenElement) document.exitFullscreen?.(); } catch { /* noop */ }
      }
      if (data.cmd === 'showTestPattern') {
        setShowTestPattern(true);
      }
      if (data.cmd === 'hideTestPattern') {
        setShowTestPattern(false);
      }
    };
    bc.postMessage({ type: 'ready' });
    return () => bc.close();
  }, [slug, goFullscreen]);

  useEffect(() => {
    const onFs = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (fs) setShowHelper(false);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Raccourci F pour passer en plein écran depuis la vitrine
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); goFullscreen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goFullscreen]);

  if (!rvp || rawSlides.length === 0) return <div className="fixed inset-0 bg-black text-white flex items-center justify-center">RVP introuvable</div>;

  const slide = slides[current];
  const Component = slide ? SLIDE_REGISTRY[slide.type] : null;
  const pollContext = sessionId ? { sessionId, slug, qIndex: current } : null;
  const currentRevealed = !!revealMap[current];
  const isOfficialSlide = slide && slide.type !== 'bienvenue-perso' && slide.type !== 'merci-perso';

  return (
    <div className="fixed inset-0 bg-[#0a0a0b] overflow-hidden" data-testid="rvp-public">
      <div className="absolute top-3 right-4 text-white/20 text-[10px] font-mono-spec uppercase tracking-widest z-10">
        codevoiture.fr · {rvp.code}{sessionId ? ` · session ${sessionId}` : ''}
      </div>
      <div className="h-screen w-screen overflow-hidden">
        <div
          data-testid="rvp-public-zoom-wrapper"
          style={{ width: `${100 / textZoom}%`, height: `${100 / textZoom}%`, transform: `scale(${textZoom})`, transformOrigin: 'top left' }}
        >
          {Component ? <Component slide={slide} key={current} pollContext={pollContext} forceReveal={currentRevealed} joinUrl={joinCode ? `${window.location.origin}/rvp/rejoindre/${joinCode}` : null} /> : <div className="h-full flex items-center justify-center text-white/50">Type inconnu</div>}
        </div>
      </div>
      <SunModeToggle />

      {/* Badge officiel formation */}
      {formationType && isOfficialSlide && (
        <div className="absolute bottom-4 right-4 pointer-events-none z-10">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md border shadow-lg ${
            formationType === 'AAC'
              ? 'border-red-500/50 bg-red-950/60 text-red-200'
              : 'border-sky-500/50 bg-sky-950/60 text-sky-200'
          }`}>
            <ShieldCheck size={11} />
            <span className="text-[10px] font-bold font-mono-spec uppercase tracking-[0.2em]">
              {formationType} · Officiel
            </span>
          </div>
        </div>
      )}

      {/* Bouton flottant Plein écran (visible quand pas en fullscreen) */}
      {!isFullscreen && (
        <button
          onClick={goFullscreen}
          data-testid="rvp-public-fullscreen"
          title="Passer en plein écran (F)"
          className="fixed top-3 left-3 z-20 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 bg-black/60 backdrop-blur-md text-white/80 hover:text-white hover:border-white/40 text-[11.5px] font-semibold transition"
        >
          <Maximize2 size={12} /> Plein écran (F)
        </button>
      )}

      {/* Overlay helper — avant premier sync OU si pas en plein écran */}
      {showHelper && !isFullscreen && (
        <div
          className="fixed inset-0 z-30 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="rvp-public-helper"
        >
          <div className="max-w-xl w-full rounded-2xl border border-red-500/40 bg-[#0d0d0e] p-6 sm:p-8 text-center">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <Projector size={11} /> Fenêtre vitrine · Vidéoprojecteur
            </div>
            <h2 className="mt-4 font-serif-editorial text-white text-[26px] sm:text-[34px] leading-tight">
              Prépare l&apos;affichage pour la salle
            </h2>
            <div className="mt-6 space-y-4 text-left">
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[12px] flex items-center justify-center shrink-0 font-bold">1</span>
                <div className="text-white/80 text-[13.5px] leading-relaxed">
                  <span className="text-white font-semibold">Glisse cette fenêtre</span> sur l&apos;écran du vidéoprojecteur (HDMI).
                  <div className="text-white/50 text-[12px] mt-1 inline-flex items-center gap-1"><MoveRight size={12} /> Attrape la barre du haut et fais-la glisser vers la droite/gauche.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[12px] flex items-center justify-center shrink-0 font-bold">2</span>
                <div className="text-white/80 text-[13.5px] leading-relaxed">
                  Clique sur <span className="text-red-300 font-semibold">« Passer en plein écran »</span> ci-dessous (ou touche <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[11px] font-mono-spec">F</kbd>).
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[12px] flex items-center justify-center shrink-0 font-bold">3</span>
                <div className="text-white/80 text-[13.5px] leading-relaxed">
                  Retourne sur ton PC — tu verras tes <span className="text-amber-300">notes formateur</span>, la salle voit uniquement les slides.
                </div>
              </div>
            </div>

            <button
              onClick={goFullscreen}
              data-testid="rvp-public-helper-fullscreen"
              className="mt-7 w-full h-14 rounded-full bg-red-600 hover:bg-red-500 text-white text-[15px] font-bold inline-flex items-center justify-center gap-2 transition shadow-lg shadow-red-900/40"
            >
              <Maximize2 size={16} /> Passer en plein écran maintenant
            </button>
            <button
              onClick={() => setShowHelper(false)}
              className="mt-2 text-white/40 hover:text-white/70 text-[12px] underline"
            >
              Masquer cette aide
            </button>
            {hasReceivedSync && (
              <div className="mt-4 text-emerald-300/80 text-[11.5px] font-mono-spec uppercase tracking-wider">
                ● Synchronisé avec le présentateur
              </div>
            )}
          </div>
        </div>
      )}
      {/* Mire de test projecteur */}
      {showTestPattern && (
        <RvpTestPattern onClose={() => {
          setShowTestPattern(false);
          try { bcRef.current?.postMessage({ cmd: 'testPatternClosed' }); } catch { /* noop */ }
        }} />
      )}
    </div>
  );
}
